// components/products/ProductRow.tsx
import { Package, DollarSign, Ruler, Award, Sparkles } from "lucide-react";
import TableRow from "../../ui/TableRow";
import { Product } from "../../../types/Product";

interface ProductRowProps {
  product: Product;
  onAction: (product: Product) => void;
  onSelect?: (product: Product, isSelected: boolean) => void;
  isSelected?: boolean;
  selectable?: boolean;
}

export default function ProductRow({ 
  product, 
  onAction, 
  onSelect,
  isSelected = false,
  selectable = false
}: ProductRowProps) {
  return (
    <TableRow
      item={product}
      onAction={onAction}
      onSelect={onSelect}
      isSelected={isSelected}
      selectable={selectable}
      actionable={true}
    >
      {/* Colonne Produit - toujours visible */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
            {product.imageUrl ? (
              <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <Package size={18} className="text-gray-400" />
            )}
          </div>
          <div>
            <span className="font-medium text-gray-800 block line-clamp-1">
              {product.name}
            </span>
            {product.badge && (
              <span className="inline-block mt-0.5 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-medium rounded">
                {product.badge}
              </span>
            )}
          </div>
        </div>
      </td>

      {/* Colonne Type - cachée sur mobile */}
      <td className="hidden sm:table-cell py-3 px-4">
        <span className="text-gray-600 text-sm">{product.type || '-'}</span>
      </td>

      {/* Colonne Prix - cachée sur mobile */}
      <td className="hidden sm:table-cell py-3 px-4">
        <div className="flex items-center gap-2">
          <DollarSign size={14} className="text-gray-400" />
          <span className="text-gray-600 font-medium">{product.priceLabel}</span>
        </div>
      </td>

      {/* Colonne Qualité - cachée sur tablette */}
      <td className="hidden md:table-cell py-3 px-4">
        <div className="flex items-center gap-2">
          <Award size={14} className="text-gray-400" />
          <span className="text-gray-600 text-sm">{product.quality || '-'}</span>
        </div>
      </td>

      {/* Colonne Texture - cachée sur tablette */}
      <td className="hidden lg:table-cell py-3 px-4">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-gray-400" />
          <span className="text-gray-600 text-sm">{product.texture || '-'}</span>
        </div>
      </td>

      {/* Colonne Longueur - cachée sur grand écran */}
      <td className="hidden xl:table-cell py-3 px-4">
        <div className="flex items-center gap-2">
          <Ruler size={14} className="text-gray-400" />
          <span className="text-gray-600 text-sm">{product.defaultLength || '-'}</span>
        </div>
      </td>

      {/* Colonne Badge - cachée sur grand écran */}
      <td className="hidden 2xl:table-cell py-3 px-4">
        {product.badge ? (
          <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
            {product.badge}
          </span>
        ) : (
          <span className="text-gray-400 text-xs">-</span>
        )}
      </td>
    </TableRow>
  );
}